<template>
  <div>
    <div class="w-full pt-4 pb-8 no-ring-form md:pb-0 md:pt-6">
      <UForm ref="formRef" :schema="schema" :state="state" class="space-y-4" @submit="onSubmit" @input="onChange">

        <div class="pt-3 pb-6 md:pb-0 md:pt-0 input-wrapper">

          <UFormGroup :label="$t('common.form.label.name')" name="vat_name" class="mb-4" eager-validation>
            <UInput size="lg" type="text" v-model="state.vat_name" class="!mt-2 bg-white" :placeholder="$t('common.form.label.name')" @keyup.enter="nif.input.focus()"/>
          </UFormGroup>

          <UFormGroup :label="$t('common.form.label.nif')" name="vat_number" class="mb-4 w-1/2 pr-1.5" eager-validation>
            <UInput ref="nif" size="lg" type="text" v-model="state.vat_number" class="!mt-2 w-full bg-white" :placeholder="$t('common.form.label.nif')" @keyup.enter="address.input.focus()"/>
          </UFormGroup>

          <UFormGroup :label="$t('common.form.label.address')" name="address" class="mb-4" eager-validation>
            <UInput ref="address" size="lg" type="text" v-model="state.address" class="!mt-2 bg-white" :placeholder="$t('common.form.label.address')" @keyup.enter="postcode.input.focus()"/>
          </UFormGroup>

          <UFormGroup :label="$t('common.form.label.postcode')" name="zip_code" class="mb-4 w-1/2 pr-1.5" eager-validation>
            <UInput ref="postcode" size="lg" type="text" v-model="state.zip_code" class="!mt-2 w-full bg-white" :placeholder="$t('common.form.label.postcode')" @keyup.enter="local.input.focus()"/>
          </UFormGroup>

          <div class="flex w-full justify-between gap-3 md:gap-4">
            <UFormGroup :label="$t('common.form.label.locality')" name="city" class="mb-4 w-1/2" eager-validation>
              <UInput ref="local" size="lg" type="text" v-model="state.city" class="!mt-2 w-full bg-white" :placeholder="$t('common.form.label.locality')" @keyup.enter="city.input.focus()"/>
            </UFormGroup>

            <UFormGroup :label="$t('common.form.label.country')" name="country" class="mb-1 w-1/2" eager-validation>
              <UInput ref="city" size="lg" type="text" v-model="state.country" class="!mt-2 w-full bg-white" :placeholder="$t('common.form.label.country')" @keyup.enter="!disabled ? formRef.submit() : null"/>
            </UFormGroup>
          </div>

        </div>
      </UForm>
      <div class="fixed bottom-0 left-0 w-full border border-neutral-200 bg-neutral-50 px-6 py-4 md:border-0 md:pt-3 md:relative md:p-0">
          <div class="flex w-full items-center justify-between gap-x-3 md:gap-x-4">
            <BaseButton v-if="!isFirstTime" @click="formRef.clear();reset()"
                        :disabled="loading"
                        type="red-secondary">
              <span class="text-base">{{ $t('common.remove') }}</span>
            </BaseButton>
            <BaseButton
                :disabled="loading || disabled || hasErrors"
                :loading="loading"
                @click="formRef.submit()"
                type="neutral">
              <span class="text-base">{{ isFirstTime ? $t('common.add') : $t('common.update')}}</span>
            </BaseButton>
          </div>
      </div>
    </div>

    <p v-show="invalid" class="pt-2 text-sm transition-all text-red">error</p>
  </div>

</template>

<script lang="ts" setup>
import { z } from "zod"
import type { FormSubmitEvent } from "#ui/types"
import {useCheckoutStore} from "~/store/checkout"
import {useRequestOptions} from "~/composables/requestOptions"
import { useSchemaMerger } from "~/composables/schema"

const {t} = useI18n()
const checkout = useCheckoutStore()
const modelValue = useAttrs().modelValue as ModalModel
modelValue.title="common.vat.title"
modelValue.icon="file"

const loading = ref(false)
const invalid = ref(false)
const disabled = ref(false)
const formRef = ref()
const nif = ref()
const address = ref()
const postcode = ref()
const local = ref()
const city = ref()

defineProps({
  title: String,
  subtitle: String,
})

const schema = useSchemaMerger([useVATNameSchema(), useVATNumberSchema(), useVATAddressSchema(), useVATZipCodeSchema(), useVATCitySchema(), useVATCountrySchema()]);

type Schema = z.output<typeof schema>

const initialState: BillingInfo = {
  vat_name: null,
  vat_number: null,
  address: null,
  zip_code: null,
  city: null,
  country: null
}

const state = reactive(initialState)

const isFirstTime = ref(true);
const hasChanged = ref(false);

const hasErrors = computed(() => {
  return formRef.value && formRef.value.getErrors().length > 0;
})

onMounted(() => {
  if (checkout.pi.billing) {
    if(Object.values(checkout.pi.billing).every(billingInfo => billingInfo === null)){
      isFirstTime.value = true;
    } else {
      state.vat_name = checkout.pi.billing.vat_name
      state.vat_number = checkout.pi.billing.vat_number
      state.address = checkout.pi.billing.address
      state.zip_code = checkout.pi.billing.zip_code
      state.city = checkout.pi.billing.city
      state.country = checkout.pi.billing.country
      isFirstTime.value = false;
    }
  }
  disabled.value = true
})

async function onSubmit (event: FormSubmitEvent<Schema>) {
  loading.value = true
  modelValue.loading = true;
  formRef.value.clear()
  const isFormNull = Object.values(event.data).every((value) => value === null);
  try {
    if(isFormNull) {
      await reset();
    } else if (hasChanged.value) {
      await updateBillingApi(event.data)
      checkout.pi.billing = JSON.parse(JSON.stringify(event.data));
      modelValue.show = false
      isFirstTime.value = false;
      disabled.value = true;
    }
  } catch (err: any) {
    if (err.statusCode === 422) {
      formRef.value.setErrors([{
        message: t(err.data.error.message),
        path: err.data.error.code,
      }])
    }
  }

  loading.value = false
  modelValue.loading = false;
}

function onChange() {
  // Set empty strings to null
  for (const [key, value] of Object.entries(state)) {
    if(value === '') {
      state[key] = null;
    }
  }

  if(hasErrors) {
    formRef.value.clear();
  }

  hasChanged.value = hasDataChanged(state, checkout.pi.billing);
  disabled.value = !hasChanged.value;
}

function hasDataChanged(info1: BillingInfo, info2: BillingInfo | null): boolean {
  if (!info2 || !info1) return true

  const keys1 = Object.keys(info1)
  const keys2 = Object.keys(info2)

  for (const key of keys1) {
    if (info1[key] !== info2[key]) {
      return true
    }
  }

  return false
}

async function reset() {
  checkout.resetBilling(initialState)
  state.vat_name = undefined
  state.vat_number = undefined
  state.address = undefined
  state.zip_code = undefined
  state.city = undefined
  state.country = undefined
  await updateBillingApi(null)
  isFirstTime.value = true;
  disabled.value = true;
  modelValue.show = false;
}

async function updateBillingApi(data: BillingInfo | null) {
  const requestOptions = useRequestOptions()
  return await $fetch(
  `/pi/${checkout.pi.id}`,
  {
    ...requestOptions,
    body: {
      billing: data
    }
  })
}
</script>

<style scoped>
 #welcome-terms-checkbox label{
   @apply text-neutral-500 font-normal pr-10
 }
 #welcome-terms-checkbox input{
   @apply w-5 h-5 border !border-neutral-300 rounded-md
 }
 .no-ring-form:deep(input){
   @apply shadow-none ring-offset-0 ring-0 border border-neutral-300 py-3 md:py-4 px-4
 }
 .no-ring-form:deep(input:focus){
   @apply shadow-none ring ring-neutral/20 border border-neutral
 }
</style>

